import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserPlus, Users, MapPin, Clock, Ship, Check, X, Ban, Settings, Trash2, Crown, Lock, Bell, AlertTriangle, Battery, BatteryCharging, BatteryLow, BatteryMedium, BatteryFull, Smartphone, Wifi, WifiOff } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { friendsAPI, alertsAPI } from '@/lib/api';
import PremiumUpgradeModal from './PremiumUpgrade';

const getMotionState = (speed) => {
  if (!speed && speed !== 0) return { label: 'Offline', color: 'bg-gray-400' };
  if (speed > 5) return { label: 'Underway', color: 'bg-green-500' };
  if (speed > 0.5) return { label: 'Drifting', color: 'bg-yellow-500' };
  return { label: 'Anchored', color: 'bg-blue-500' };
};

// Battery icon helper
const getBatteryIcon = (level, isCharging) => {
  if (level === null || level === undefined) {
    return <Battery className="w-4 h-4 text-gray-400" />;
  }
  if (isCharging) {
    return <BatteryCharging className="w-4 h-4 text-green-500" />;
  }
  if (level < 0.2) {
    return <BatteryLow className="w-4 h-4 text-red-500" />;
  }
  if (level < 0.5) {
    return <BatteryMedium className="w-4 h-4 text-yellow-500" />;
  }
  return <BatteryFull className="w-4 h-4 text-green-500" />;
};

// App state helper
const getAppStateInfo = (appState, updatedAt) => {
  // Check if location is stale (> 5 minutes old)
  const isStale = updatedAt && (new Date() - new Date(updatedAt)) > 5 * 60 * 1000;
  
  if (isStale) {
    return { icon: <WifiOff className="w-3 h-3 text-gray-400" />, label: 'Offline' };
  }
  if (appState === 'background') {
    return { icon: <Smartphone className="w-3 h-3 text-yellow-500" />, label: 'Background' };
  }
  return { icon: <Wifi className="w-3 h-3 text-green-500" />, label: 'Active' };
};

const CrewScreen = ({ onViewOnMap }) => {
  const {
    friends,
    friendRequests,
    fetchFriends,
    sendFriendRequest,
    respondToFriendRequest,
    removeFriend,
    isPaid,
    upgradeSubscription,
    alerts,
    fetchAlerts,
  } = useApp();

  const [addFriendOpen, setAddFriendOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [permissions, setPermissions] = useState({
    share_location: true,
    share_speed: true,
    share_trip_only: false,
  });
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  useEffect(() => {
    if (isPaid) {
      fetchFriends();
      fetchAlerts();
    }
  }, [fetchFriends, fetchAlerts, isPaid]);

  // Premium gate for free users
  if (!isPaid) {
    return (
      <div className="p-4 flex flex-col items-center justify-center min-h-[60vh]" data-testid="crew-screen">
        <PremiumUpgradeModal 
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          onUpgrade={async () => {
            await upgradeSubscription();
            setShowPremiumModal(false);
          }}
          blockedFeature="Friends & Family Tracking"
        />
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-10 h-10 text-cyan-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Friends & Family</h2>
          <p className="text-gray-600 mb-6">
            Add friends and family to track each other in real-time on the water. See their location, speed, and motion state.
          </p>
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-cyan-800 flex items-center gap-2 justify-center">
              <Crown className="w-4 h-4" />
              This feature requires Boat 360 Plus
            </p>
          </div>
          <Button 
            onClick={() => setShowPremiumModal(true)}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
            data-testid="upgrade-crew-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to Plus
          </Button>
        </div>
      </div>
    );
  }

  const handleAddFriend = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await sendFriendRequest(username);
      setUsername('');
      setAddFriendOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRespond = async (requestId, action) => {
    try {
      await respondToFriendRequest(requestId, action);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (window.confirm('Remove this friend?')) {
      try {
        await removeFriend(friendId);
      } catch (error) {
        toast.error('Failed to remove friend');
      }
    }
  };

  const openPermissions = (friend) => {
    setSelectedFriend(friend);
    setPermissions({
      share_location: friend.share_location,
      share_speed: friend.share_speed,
      share_trip_only: friend.share_trip_only,
    });
    setPermissionsOpen(true);
  };

  const savePermissions = async () => {
    if (!selectedFriend) return;
    try {
      await friendsAPI.updatePermissions(selectedFriend.friend_user_id, permissions);
      toast.success('Permissions updated');
      fetchFriends();
      setPermissionsOpen(false);
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const clearAlert = async (alertId) => {
    try {
      await alertsAPI.markRead(alertId);
      fetchAlerts();
      toast.success('Alert cleared');
    } catch (error) {
      toast.error('Failed to clear alert');
    }
  };

  const clearAllAlerts = async () => {
    try {
      await alertsAPI.clearAll();
      fetchAlerts();
      toast.success('All alerts cleared');
    } catch (error) {
      toast.error('Failed to clear alerts');
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="crew-screen">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Crew</h1>
            <p className="text-sm text-gray-500">{friends.length} friends</p>
          </div>
          <Dialog open={addFriendOpen} onOpenChange={setAddFriendOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600" data-testid="add-friend-btn">
                <UserPlus className="w-4 h-4 mr-1" /> Add Friend
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleAddFriend}>
                <DialogHeader>
                  <DialogTitle>Add Friend</DialogTitle>
                  <DialogDescription>
                    Enter their username to send a friend request
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    placeholder="captainjack"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                    className="mt-2"
                    data-testid="friend-username-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAddFriendOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={!username || isLoading} data-testid="send-request-btn">
                    {isLoading ? 'Sending...' : 'Send Request'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <ScrollArea className="flex-1 px-4 py-4">
        {/* Alerts */}
        {alerts && alerts.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                Alerts ({alerts.length})
              </h2>
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-gray-500 h-6"
                onClick={clearAllAlerts}
                data-testid="clear-all-alerts"
              >
                Clear All
              </Button>
            </div>
            <div className="space-y-3">
              {alerts.map((alert) => (
                <Card key={alert.id} className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        <div>
                          <p className="font-medium text-gray-800">{alert.alert_type || 'Alert'}</p>
                          <p className="text-sm text-gray-600">{alert.message || 'No message'}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {alert.created_at ? formatDistanceToNow(new Date(alert.created_at)) + ' ago' : 'Just now'}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => clearAlert(alert.id)}
                        data-testid={`clear-alert-${alert.id}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Pending Requests */}
        {friendRequests.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Pending Requests ({friendRequests.length})
            </h2>
            <div className="space-y-3">
              {friendRequests.map((request) => (
                <Card key={request.id} className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{request.from_display_name}</p>
                        <p className="text-sm text-gray-500">@{request.from_username}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 h-8"
                          onClick={() => handleRespond(request.id, 'ACCEPTED')}
                          data-testid={`accept-${request.id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => handleRespond(request.id, 'DECLINED')}
                          data-testid={`decline-${request.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-red-500 hover:text-red-600"
                          onClick={() => handleRespond(request.id, 'BLOCKED')}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Friends List */}
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Your Friends
          </h2>
          {friends.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No friends yet</p>
                <p className="text-sm text-gray-400">Add friends to see them on the map</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {friends.filter((f) => f.is_overdue).length > 0 && (
                <div
                  className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5"
                  data-testid="crew-overdue-banner"
                >
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <span className="font-semibold">
                      {friends.filter((f) => f.is_overdue).length} crew member
                      {friends.filter((f) => f.is_overdue).length > 1 ? 's have' : ' has'} gone quiet.
                    </span>{' '}
                    No location update in over 4 hours — you may want to check on them.
                  </div>
                </div>
              )}
              {friends.map((friend) => {
                const motion = getMotionState(friend.speed);
                const batteryIcon = getBatteryIcon(friend.battery_level, friend.is_charging);
                const appStateInfo = getAppStateInfo(friend.app_state, friend.location_updated_at);
                
                return (
                  <Card key={friend.friend_user_id} data-testid={`friend-${friend.friend_user_id}`} className={friend.is_overdue ? 'border-amber-400 bg-amber-50/40' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                            <Ship className="w-6 h-6 text-cyan-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{friend.display_name}</p>
                            <p className="text-sm text-gray-500">@{friend.username}</p>
                            {friend.is_overdue && (
                              <Badge
                                variant="outline"
                                className="mt-1 text-xs border-amber-500 text-amber-700 bg-amber-100 gap-1"
                                data-testid={`overdue-badge-${friend.friend_user_id}`}
                              >
                                <AlertTriangle className="w-3 h-3" />
                                No signal for {Math.max(1, Math.floor((friend.minutes_since_update || 0) / 60))}h
                              </Badge>
                            )}
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge className={`${motion.color} text-white text-xs`}>
                                {motion.label}
                              </Badge>
                              {friend.speed !== null && friend.speed !== undefined && (
                                <span className="text-xs text-gray-500">
                                  {friend.speed.toFixed(1)} kt
                                </span>
                              )}
                              {friend.is_on_trip && (
                                <Badge variant="outline" className="text-xs">
                                  On Trip
                                </Badge>
                              )}
                            </div>
                            {/* Battery & App Status Row */}
                            <div className="flex items-center gap-3 mt-1">
                              {friend.battery_level !== null && friend.battery_level !== undefined && (
                                <div className="flex items-center gap-1" title={friend.is_charging ? 'Charging' : 'On battery'}>
                                  {batteryIcon}
                                  <span className="text-xs text-gray-500">
                                    {Math.round(friend.battery_level * 100)}%
                                  </span>
                                </div>
                              )}
                              <div className="flex items-center gap-1" title={appStateInfo.label}>
                                {appStateInfo.icon}
                                <span className="text-xs text-gray-400">{appStateInfo.label}</span>
                              </div>
                            </div>
                            {friend.location_updated_at && (
                              <p className="text-xs text-gray-400 mt-1">
                                <Clock className="w-3 h-3 inline mr-1" />
                                Updated {formatDistanceToNow(new Date(friend.location_updated_at))} ago
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          {friend.lat && friend.lon && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => onViewOnMap && onViewOnMap(friend)}
                              data-testid={`view-map-${friend.friend_user_id}`}
                            >
                              <MapPin className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            onClick={() => openPermissions(friend)}
                          >
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-red-500 hover:text-red-600"
                            onClick={() => handleRemoveFriend(friend.friend_user_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Permissions Dialog */}
      <Dialog open={permissionsOpen} onOpenChange={setPermissionsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sharing with {selectedFriend?.display_name}</DialogTitle>
            <DialogDescription>
              Control what information you share with this friend
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="share-location">Share my location</Label>
              <Switch
                id="share-location"
                checked={permissions.share_location}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, share_location: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="share-speed">Share my speed</Label>
              <Switch
                id="share-speed"
                checked={permissions.share_speed}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, share_speed: checked })
                }
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="trip-only">Share only during trips</Label>
              <Switch
                id="trip-only"
                checked={permissions.share_trip_only}
                onCheckedChange={(checked) =>
                  setPermissions({ ...permissions, share_trip_only: checked })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={savePermissions}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrewScreen;
